import { useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { googleLogout } from '@react-oauth/google';

import { useAuthStore } from '@/shared/store/auth.store';
import { useCryptoStore } from '@/shared/store/crypto.store';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';

import { apiConfig } from '../config';
import { RequestParams } from '../generated/http-client';
import { isAuthError } from '../helpers/errors';
import {
  ApiCreateDocumentDtoV2,
  ApiDownloadDocumentDto,
  ApiLegacyTokenDto,
  ApiLoginDto,
  ApiPinOprfEvaluateDto,
  ApiPinSetupDto,
  ApiPinUnlockDto,
  ApiRefreshTokenDto,
} from '../types';

import { useApiBase } from './useApiBase';

export const useApi = () => {
  const api = useApiBase();
  const apolloClient = useApolloClient();
  const { isBeneficiary } = useProfileStore();
  const authStore = useAuthStore();
  const keyPairStore = useKeyPairStore();
  const cryptoStore = useCryptoStore();

  return useMemo(
    () => ({
      /* Account */
      me: async (authorization?: string) => {
        const options = authorization ? { headers: { authorization } } : undefined;
        const res = await api('account').accountControllerMe(options);
        const searchParams = new URLSearchParams();

        if (res.error?.errorCode && isAuthError(res.error.errorCode)) {
          searchParams.set('sessionExpired', '1');

          googleLogout();
          authStore.clear();
          keyPairStore.clear();
          cryptoStore.clear();
          await apolloClient.resetStore();

          window.location.href = isBeneficiary
            ? `/auth/login?${searchParams.toString()}`
            : `/auth/login/personal-account?${searchParams.toString()}`;
        }

        return res;
      },

      /* Auth */
      loginViaPersonalAccountCode: api('auth').authControllerLoginViaPersonalAccountCode,
      beneficiaryRemove: api('auth').authControllerRemoveBeneficiaryProfile,
      login: api('auth').authControllerLogin,
      beneficiaryInvite: api('auth').authControllerSendNewInviteToBeneficiary,
      activateAccount: api('auth').authControllerActivateAccount,
      generateEmailCode: api('auth').authControllerGenerateEmailCode,
      /* Profiles */
      profilesGet: api('profiles').profileControllerGetProfile,
      profilesGetBeneficiaries: api('profiles').profileControllerGetBeneficiaries,
      profilesUpdate: api('profiles').profileControllerUpdateProfile,
      profilesChangeBaseCurrency: api('profiles').profileControllerChangeBaseCurrency,

      /* Synchronizer */
      collectionsRemoveMany: api('synchronizer').synchronizerControllerRemoveItems,

      /* Myhub */
      myhubGetRegions: api('myhubRegions').regionNameControllerGetAvailableRegions,
      myhubGetSettings: api('hubSettings').hubSettingsControllerGetHubSettings,

      /* Scenarios */
      getScenariosOfBeneficiaries: api('scenarios').scenarioControllerGetAllFamilyMemberScenarios,

      /* Subscription */
      subscriptionGetStatus: api('subscriptions').subscriptionControllerGetStatus,

      /* Will */
      willsFindAll: api('wills').willControllerFindAll,
      willsGet: api('wills').willControllerFindOne,
      willsCreate: api('wills').willControllerCreate,
      willsRemove: api('wills').willControllerRemove,
      willsGetParticipants: api('wills').willControllerGetParticipants,
      willsCreateParticipant: api('wills').willControllerAddParticipant,
      willsRemoveParticipant: api('wills').willControllerRemoveParticipant,
      willsAddAttachment: api('wills').willControllerAddAttachment,
      willsUpdateAttachment: api('wills').willControllerUpdateAttachment,
      willsAttachScenario: api('wills').willControllerAttachScenario,

      /* Mail Server */
      mailServerGetMailboxes: api('mailServer').mailServerControllerGetAllMailboxes,
      /* Currencies */
      getCurrencyRate: api('currencies').currencyControllerGetCurrencyRates,
      /* File Storage */
      filesGet: (filename: string, params?: RequestParams) => {
        return api('fileStorage').fileStorageControllerGetFile(filename, {
          baseUrl: apiConfig.directUrl,
          ...params,
        });
      },
      filesDeleteFile: api('fileStorage').fileStorageControllerDeleteFile,
      /* Gtw Scenario */
      getScenario: api('gtwScenarios').scenarioControllerFindOne,
      scenariosList: api('gtwScenarios').scenarioControllerFindAll,
      scenarioCreate: api('gtwScenarios').scenarioControllerCreate,
      scenarioEdit: api('gtwScenarios').scenarioControllerUpdate,
      scenarioDelete: api('gtwScenarios').scenarioControllerDelete,
      scenarioActivate: api('gtwScenarios').scenarioControllerActivate,
      scenarioCancel: api('gtwScenarios').scenarioControllerCancel,
      scenarioRunImmediately: api('gtwScenarios').scenarioControllerRunImmediately,
      scenarioIncomingTransfersList: api('gtwScenarios').transferControllerGetIncoming,
      scenarioAcceptTransfer: api('gtwScenarios').transferControllerAccept,
      scenarioAcceptTransferAll: api('gtwScenarios').transferControllerAcceptAll,
      scenarioShared: api('gtwScenarios').scenarioControllerFindShared,
      scenarioUpdateCollectionItemId: api('gtwScenarios').scenarioControllerUpdateCollectionItemId,

      /* Gtw auth */
      authLogin: async (data: ApiLoginDto) =>
        await api('gtwAuth').authLogin(data, { credentials: 'include' }),

      authLogout: async (data: ApiRefreshTokenDto = {}) =>
        await api('gtwAuth').authLogout(data, { credentials: 'include' }),

      authLegacyToken: async (data: ApiLegacyTokenDto) =>
        await api('gtwAuth').authLegacy(data, { credentials: 'include' }),
      refreshAuthToken: async (data: ApiRefreshTokenDto = {}) =>
        await api('gtwAuth').authRefresh(data, { credentials: 'include' }),

      authGoogleVerifyCode: api('gtwAuth').authGoogleVerifyCode,
      updateUser: api('gtwAuth').authUpdateUser,
      scopesGetAll: api('gtwAuth').scopeControllerFindAll,
      scopesInvitations: api('gtwAuth').invitationControllerFindAll,
      scopesDelete: api('gtwAuth').scopeControllerDelete,
      scopeControllerFindTenantProfiles: api('gtwAuth').scopeControllerFindTenantProfiles,
      scopeCreate: api('gtwAuth').scopeControllerCreate,
      vfoInviteMember: api('gtwAuth').invitationControllerCreateVfo,
      vfoInvitationResend: api('gtwAuth').invitationControllerResend,
      vfoInvitationDelete: api('gtwAuth').invitationControllerDelete,
      validateInvitationToken: api('gtwAuth').invitationControllerValidateToken,
      register: api('gtwAuth').authRegister,
      verifyEmailByOtp: api('gtwAuth').authVerifyEmailByOtp,
      authMe: async (authorization?: string) => {
        const options = authorization ? { headers: { authorization } } : undefined;
        const res = await api('gtwAuth').authMe(options);
        return res;
      },
      requestEmailVerification: api('gtwAuth').authRequestEmailVerification,
      completeOnboarding: api('gtwAuth').authOnboardingCompleted,
      evaluatePinOprf: async (data: ApiPinOprfEvaluateDto, params?: RequestParams) => {
        return await api('gtwAuth').authPinOprfEvaluate(data, {
          ...params,
          credentials: 'include',
        });
      },
      setupPin: async (data: ApiPinSetupDto) => {
        return await api('gtwAuth').authPinSetup(data, { credentials: 'include' });
      },
      unlockPin: async (data: ApiPinUnlockDto) => {
        return await api('gtwAuth').authPinUnlock(data, { credentials: 'include' });
      },

      /* Gtw llmSocket */
      aiBatchFilesByUri: api('gtwLlmSocket').httpExtractBatchByUriExtractBatchByUriPost,
      aiExtractEntityByUri:
        api('gtwLlmSocket').httpExtractDocumentFromUrisExtractDocumentFromUrisPost,
      aiExtractFromImages: api('gtwLlmSocket').httpExtractFromImagesExtractFromImagesPost,
      aiGetJob: api('gtwLlmSocket').httpGetJobJobsJobIdGet,
      aiGetJobs: api('gtwLlmSocket').httpListJobsBySubJobsBySubGet,

      /* Gtw Video Certificates */
      videoCertificatesGet: api('gtwCertificates').videoCertificateControllerGetVideoById,
      videoCertificatesGetAll:
        api('gtwCertificates').videoCertificateControllerGetAllVideosByWillId,
      videoCertificateCreate: api('gtwCertificates').videoCertificateControllerSignVideo,

      /* Gtw Case */
      casesGetAll: api('gtwCase').caseControllerV2FindAll,
      casesGetOne: api('gtwCase').caseControllerV2FindOne,
      casesCreate: api('gtwCase').caseControllerV2Create,
      casesUpdate: api('gtwCase').caseControllerV2Update,
      casesDelete: api('gtwCase').caseControllerV2Delete,
      casesAddMember: api('gtwCase').caseControllerV2AddMember,
      casesRemoveMember: api('gtwCase').caseControllerV2RemoveMember,
      casesGetMembers: api('gtwCase').caseMemberControllerV2FindAll,
      casesUpdateMember: api('gtwCase').caseMemberControllerV2Update,
      casesUpdateSelf: api('gtwCase').caseMemberControllerV2UpdateSelf,
      documentsGetAll: api('gtwCase').documentControllerV2FindAll,
      documentsAddDocuments: api('gtwCase').documentControllerV2AddToCase,
      documentsRemoveFromCase: api('gtwCase').documentControllerV2RemoveFromCase,
      documentsDelete: api('gtwCase').documentControllerV2Delete,
      documentsCreate: (data: ApiCreateDocumentDtoV2, params?: RequestParams) => {
        return api('gtwCase').documentControllerV2Create(data, {
          baseUrl: apiConfig.url.replace('api', 'case'),
          path: '/v2/documents',
          ...params,
        });
      },
      documentsDownload: (data: ApiDownloadDocumentDto, params?: RequestParams) => {
        return api('gtwCase').documentControllerV2Download(data, {
          baseUrl: apiConfig.url.replace('api', 'case'),
          path: `/v2/documents/download`,
          ...params,
        });
      },
      documentsMarkAsRead: api('gtwCase').documentControllerV2MarkAsRead,
      dashboardGetAll: api('gtwCase').dashboardControllerV2GetDashboard,
      tasksGetAll: api('gtwCase').taskControllerV2FindAll,
      tasksCreate: api('gtwCase').taskControllerV2Create,
      tasksUpdate: api('gtwCase').taskControllerV2Update,
      tasksDelete: api('gtwCase').taskControllerV2Delete,
      taskAddDocuments: api('gtwCase').taskControllerV2AddDocuments,
      taskRemoveDocument: api('gtwCase').taskControllerV2RemoveDocument,

      /* Gtw Chat */
      chatsFindAll: api('gtwChat').chatControllerFindAll,
      chatsMessageFindAll: api('gtwChat').messageControllerFindAll,
      chatsMessageCreate: api('gtwChat').messageControllerCreate,
      chatsMessageFindOne: api('gtwChat').messageControllerFindOne,
      deleteChat: api('gtwChat').chatControllerDelete,

      /* Gtw Permissions */
      permissionsGetAll: api('gtwCase').permissionControllerGetAllPermissions,
      permissionsGetAllRoles: api('gtwCase').permissionControllerGetAllRoles,
      permissionsGetRolePermissions: api('gtwCase').permissionControllerGetRolePermissions,
      permissionsGetUserPermissions: api('gtwCase').permissionControllerGetUserPermissions,
      permissionsGetUserRoles: api('gtwCase').permissionControllerGetUserRoles,
      permissionsGrantPermission: api('gtwCase').permissionControllerGrantPermission,
      permissionsRevokePermission: api('gtwCase').permissionControllerRevokePermission,
      permissionsGrantRole: api('gtwCase').permissionControllerGrantRole,
      permissionsRevokeRole: api('gtwCase').permissionControllerRevokeRole,
      permissionsGetChat: api('gtwCase').permissionControllerGetChatPermissions,
      permissionsUpdateChat: api('gtwCase').permissionControllerUpdateChatPermissions,
    }),
    [api],
  );
};
